import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import StoryCard from '@/components/base/StoryCard';
import { SITE_NAME, SITE_ORIGIN, useSEO } from '@/hooks/useSEO';
import { categories, filterStories, getCategoryName } from '@/lib/content';

const HOT_JSON_LD = {
  '@context': 'https://schema.org',
  '@type': 'CollectionPage',
  name: '热门速览列表 - 5看料网',
  url: `${SITE_ORIGIN}/hot`,
  inLanguage: 'zh-CN',
  isPartOf: { '@type': 'WebSite', name: SITE_NAME, url: `${SITE_ORIGIN}/` },
  breadcrumb: {
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: `${SITE_ORIGIN}/` },
      { '@type': 'ListItem', position: 2, name: '热门速览', item: `${SITE_ORIGIN}/hot` },
    ],
  },
};

export default function Hot() {
  const [searchParams, setSearchParams] = useSearchParams();
  const category = searchParams.get('category') || 'all';
  const query = searchParams.get('q') || '';
  const [keyword, setKeyword] = useState(query);

  useEffect(() => {
    setKeyword(query);
  }, [query]);

  const list = filterStories({ category, keyword: query });
  const activeName = category === 'all' ? '全部速览' : getCategoryName(category);

  useSEO({
    title: category === 'all' ? '热门速览列表 | 5看料网速览快看' : `${activeName}速览 | 5看料网`,
    description: `浏览5看料网${activeName}板块的全部速览内容，每条都包含速览要点与精简摘要，帮助你在碎片时间快速掌握热点核心信息。`,
    keywords: `5看料网速览,${activeName},热点概览,精简摘要,速览列表`,
    path: '/hot',
    jsonLd: HOT_JSON_LD,
  });

  const updateParams = (next: { category?: string; q?: string }) => {
    const params = new URLSearchParams();
    const nextCategory = next.category ?? category;
    const nextQuery = next.q ?? query;
    if (nextCategory && nextCategory !== 'all') params.set('category', nextCategory);
    if (nextQuery) params.set('q', nextQuery);
    setSearchParams(params);
  };

  const handleSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    updateParams({ q: keyword.trim() });
  };

  return (
    <div className="w-full">
      <section className="w-full border-b border-background-200 bg-background-100 py-10 md:py-14">
        <div className="w-full px-4 md:px-8">
          <nav aria-label="面包屑" className="flex flex-wrap items-center gap-2 font-body text-xs text-foreground-500">
            <Link to="/" className="transition-colors hover:text-primary-600">
              首页
            </Link>
            <i className="ri-arrow-right-s-line text-sm" aria-hidden="true"></i>
            <span className="text-foreground-800">热门速览</span>
            {category !== 'all' && (
              <>
                <i className="ri-arrow-right-s-line text-sm" aria-hidden="true"></i>
                <span className="text-foreground-800">{activeName}</span>
              </>
            )}
          </nav>

          <div className="mt-5 flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
            <div className="max-w-2xl">
              <h1 className="font-heading text-3xl font-black leading-tight text-foreground-950 md:text-4xl">
                {activeName}
              </h1>
              <p className="mt-3 font-body text-sm leading-relaxed text-foreground-600 md:text-base">
                共 {list.length} 条速览。每条内容都按照"速览要点 → 精简摘要 → 完整正文"的顺序组织，你可以只读前两层。
              </p>
            </div>

            <form
              onSubmit={handleSearch}
              role="search"
              className="flex w-full items-center rounded-md border border-background-300 bg-background-50 px-3 py-2.5 lg:w-80"
            >
              <i className="ri-search-line text-base text-foreground-500" aria-hidden="true"></i>
              <input
                value={keyword}
                onChange={(event) => setKeyword(event.target.value)}
                type="search"
                placeholder="搜索标题、摘要或标签"
                aria-label="搜索速览内容"
                className="ml-2 w-full bg-transparent font-body text-sm text-foreground-900 outline-none placeholder:text-foreground-500"
              />
              <button
                type="submit"
                className="ml-2 shrink-0 cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-3 py-1.5 font-label text-xs font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                搜索
              </button>
            </form>
          </div>
        </div>
      </section>

      <section className="w-full py-10 md:py-14">
        <div className="w-full px-4 md:px-8">
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => updateParams({ category: 'all' })}
              className={`cursor-pointer whitespace-nowrap rounded-full px-4 py-1.5 font-label text-sm transition-colors ${
                category === 'all'
                  ? 'bg-primary-500 text-background-50'
                  : 'border border-background-300 bg-background-50 text-foreground-700 hover:border-primary-300 hover:text-primary-600'
              }`}
            >
              全部
            </button>
            {categories.map((item) => (
              <button
                key={item.key}
                type="button"
                onClick={() => updateParams({ category: item.key })}
                className={`cursor-pointer whitespace-nowrap rounded-full px-4 py-1.5 font-label text-sm transition-colors ${
                  category === item.key
                    ? 'bg-primary-500 text-background-50'
                    : 'border border-background-300 bg-background-50 text-foreground-700 hover:border-primary-300 hover:text-primary-600'
                }`}
              >
                {item.name}
              </button>
            ))}
          </div>

          {query && (
            <p className="mt-6 font-body text-sm text-foreground-600">
              关键词「<span className="font-medium text-primary-600">{query}</span>」的搜索结果
              <button
                type="button"
                onClick={() => updateParams({ q: '' })}
                className="ml-3 cursor-pointer font-label text-xs text-foreground-500 underline transition-colors hover:text-primary-600"
              >
                清除
              </button>
            </p>
          )}

          {list.length > 0 ? (
            <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {list.map((story, index) => (
                <StoryCard key={story.id} story={story} rank={index + 1} />
              ))}
            </div>
          ) : (
            <div className="mt-10 rounded-lg border border-background-300 bg-background-100 p-10 text-center">
              <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-background-200 text-foreground-500">
                <i className="ri-inbox-line text-2xl" aria-hidden="true"></i>
              </span>
              <p className="mt-4 font-heading text-base font-semibold text-foreground-900">
                没有找到匹配的速览
              </p>
              <p className="mt-2 font-body text-sm text-foreground-600">
                换个关键词，或者切换到其他板块看看。
              </p>
              <button
                type="button"
                onClick={() => setSearchParams(new URLSearchParams())}
                className="mt-5 cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 font-label text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                查看全部速览
              </button>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}